/**
 * Stale-while-revalidate fetcher backed by localStorage.
 *
 * Why this exists: every page in this app does
 *
 *     const [data, setData] = useState(null);
 *     const [loading, setLoading] = useState(true);
 *     useEffect(() => { api.get(...).then(...).finally(...) }, [filters]);
 *
 * which means every navigation shows a spinner from scratch, even for data
 * the user just looked at 30 seconds ago. This hook keeps the LAST response
 * for a given cache key in localStorage. On mount it returns the cached
 * value immediately (so the page renders instantly with the user's prior
 * data), then fires the network call in the background and silently swaps
 * in the fresh data when it lands.
 *
 * USAGE
 *   const { data, loading, stale, error, refetch } = useCachedRequest({
 *     key: `overview:summary:${timeframe}`,
 *     fetcher: () => api.get("/dashboard/summary", { timeframe }),
 *     deps: [timeframe],
 *   });
 *
 * NOTES
 * - The cache key MUST include any filter/pagination state. Two different
 *   filters with the same key would silently overwrite each other.
 * - `loading` is only true on first paint when no cached value exists. On
 *   subsequent visits with a cached hit it stays false — you don't need to
 *   render a spinner. Use `stale` if you want to show a tiny "refreshing"
 *   indicator while the background fetch is in flight.
 * - We cap stored payloads at MAX_BYTES_PER_KEY to stay under the ~5MB
 *   localStorage budget. Big responses (e.g. transaction lists) are simply
 *   not cached — the page falls back to the normal first-paint spinner.
 * - Entries older than MAX_AGE_MS are treated as cache misses (we don't
 *   want to show data from a month ago).
 */

import { useCallback, useEffect, useRef, useState } from "react";

const CACHE_PREFIX = "bl-cache:v1:";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;  // 7d ceiling
const MAX_BYTES_PER_KEY = 300_000;            // ~300KB per key — keep total comfortably under 5MB localStorage cap

function storageKey(key) {
  return CACHE_PREFIX + key;
}

export function readCache(key) {
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.writtenAt !== "number") return null;
    if (Date.now() - parsed.writtenAt > MAX_AGE_MS) {
      localStorage.removeItem(storageKey(key));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeCache(key, data) {
  try {
    const payload = JSON.stringify({ writtenAt: Date.now(), data });
    if (payload.length > MAX_BYTES_PER_KEY) {
      // Too big — clear any old entry and bail. Page will treat as miss next time.
      localStorage.removeItem(storageKey(key));
      return false;
    }
    localStorage.setItem(storageKey(key), payload);
    return true;
  } catch {
    // Quota exceeded / disabled / etc. — skip silently.
    return false;
  }
}

/**
 * Wipe all cache entries matching a prefix. Use after a mutation that
 * invalidates one or more pages' worth of data.
 *
 *   invalidateCache("transactions:")   // clears every transactions:* key
 *   invalidateCache()                  // clears the entire app cache
 */
export function invalidateCache(prefix = "") {
  try {
    const full = CACHE_PREFIX + prefix;
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(full)) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

/**
 * Hook. See module docstring for usage notes.
 *
 * The fetcher should return the data shape you want stored — most of the
 * time that's the unwrapped response from `api.get(...)` (our axios
 * interceptor already strips `.data` off, so you get the JSON body directly).
 */
export function useCachedRequest({ key, fetcher, deps = [], enabled = true }) {
  const [state, setState] = useState(() => {
    if (!enabled) return { data: null, loading: false, stale: false, error: null };
    const cached = readCache(key);
    return cached
      ? { data: cached.data, loading: false, stale: true, error: null }
      : { data: null, loading: true, stale: false, error: null };
  });

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const run = useCallback(async (currentKey) => {
    if (!enabled) return;
    setState((s) => ({ ...s, loading: !s.data, stale: !!s.data, error: null }));
    try {
      const data = await fetcherRef.current();
      if (!aliveRef.current) return;
      writeCache(currentKey, data);
      setState({ data, loading: false, stale: false, error: null });
    } catch (err) {
      if (!aliveRef.current) return;
      setState((s) => ({ data: s.data, loading: false, stale: !!s.data, error: err }));
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    // When deps change (e.g. user switched filter), re-key the cache view too.
    const cached = readCache(key);
    setState(cached
      ? { data: cached.data, loading: false, stale: true, error: null }
      : { data: null, loading: true, stale: false, error: null }
    );
    run(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, ...deps]);

  const refetch = useCallback(() => run(key), [run, key]);

  return { ...state, refetch };
}
